import "./narrative.scss";

import { List } from "immutable";
import _ from "lodash";
import PropTypes from "prop-types";
import PubSub from "pubsub-js";
import React, { PureComponent } from "react";
import { findDOMNode } from "react-dom";
import ImmutablePropTypes from "react-immutable-proptypes";
import { connect } from "react-redux";

import { withScreenContext } from "../../components/screen/ScreenContext";
import SplitView from "../../components/splitView";
import { GLOBAL_FILTER_ID } from "../../constants/identifiers";
import interfaceMessages from "../../constants/interfaceMessages";
import interfaceUserActions from "../../constants/interfaceUserActions";
import PubSubTopics from "../../constants/pubSubTopics";
import Dimensions from "../../constants/styles/dimensions";
import { appStateOperations, appStateSelectors } from "../../modules/appState";
import batchOperations from "../../modules/batchOperations";
import batchUtils from "../../modules/batchUtils";
import { definitionSelectors } from "../../modules/definition";
import { colorSelectors } from "../../modules/definition/colors";
import { narrativeSettingsSelectors } from "../../modules/definition/narrativeSettings";
import { propertySelectors } from "../../modules/definition/properties";
import { referenceSelectors } from "../../modules/definition/references";
import { typeSelectors } from "../../modules/definition/types";
import dropSelectors from "../../modules/dropSelectors";
import { filterSelectors } from "../../modules/filters";
import { itemOperations, itemSelectors } from "../../modules/items";
import { narrativeOperations } from "../../modules/narrative";
import { uiStateOperations, uiStateSelectors } from "../../modules/uiState";
import { shouldAutoEditItems } from "../../modules/undoRedo";
import { immutableRecords, PropTypeShapes } from "../../types";
import { mergeDispatchToProps } from "../../utils/codeSplit";
import { onPasteItems } from "../../utils/copyPaste";
import hasAncestorOfClass from "../../utils/hasAncestorOfClass";
import { listToIndexMap } from "../../utils/immutableExtensions";
import { updateSelectedItemsUsingUpdaters } from "../../utils/selection";
// import debugUtils from "../../utils/debugUtils";
import { trackEvent } from "../../utils/trackAnalytics";
import { appSelectors } from "../app";
import { withWorkspaceContext } from "../workspace/WorkspaceContext";
import NarrativeCanvasView from "./narrativeCanvasView";
import selectors from "./selectors";

class NarrativeView extends PureComponent {
  constructor(props, context) {
    super(props, context);

    this.state = {
      displayData: immutableRecords.NarrativeDisplayRecord({})
    };
    this._lastAction = null;
    this._lastDraggedItemIds = null;
    this.internalMoveCard = this.internalMoveCard.bind(this);
    this.internalDragLeave = this.internalDragLeave.bind(this);
    this.dispatchLastMoveAction = this.dispatchLastMoveAction.bind(this);
    this.dragPreviewDataForItemId = this.dragPreviewDataForItemId.bind(this);
    this.requestCanDropItems = this.requestCanDropItems.bind(this);
    this.onItemClick = this.onItemClick.bind(this);
    this.requestViewBoundsForZoom = this.requestViewBoundsForZoom.bind(this);
    this.onCreateItem = this.onCreateItem.bind(this);
    this.onCreateLeafItem = this.onCreateLeafItem.bind(this);
    this.onSelectAvailableItem = this.onSelectAvailableItem.bind(this);

    // copy paste
    this._pubSubPasteToken = null;
    this.onPasteMessageReceived = this.onPasteMessageReceived.bind(this);
    this.onPaste = this.onPaste.bind(this);
    this.processPastingItems = this.processPastingItems.bind(this);

    // For embedded narrative cards click events propagation
    this._clickedCard = null;
  }

  componentWillMount() {
    this.copyLocalStore(this.props);
  }

  componentDidMount() {
    this._node = findDOMNode(this);
    this._pubSubPasteToken = PubSub.subscribe(PubSubTopics.paste, this.onPasteMessageReceived);
    this.props.workViewRegistration.registerWorkViewCreateItemCallback(this.props.viewId, this.onCreateItem);
  }

  componentWillReceiveProps(nextProps) {
    // debugUtils.logPropChanges("NarrativeView", this.props, nextProps, true);
    if (nextProps.sortedItemIds !== this.props.sortedItemIds) {
      this._lastClickItemId = null;
      this._firstShiftClickSelection = null;
    }
    this.copyLocalStore(nextProps);
  }

  componentWillUnmount() {
    PubSub.unsubscribe(this._pubSubPasteToken);
    this.props.workViewRegistration.unregisterWorkViewCreateItemCallback(this.props.viewId);
  }

  canCreateItemOfType(typeId) {
    return typeSelectors.getTypeIsAllowedInNarrative(this.props.localStore, typeId);
  }

  processPastingItems(itemMapList) {
    const visibleSelectedIds = this.props.selectedItemIds.filter(i => this.props.narrativeIndexById.has(i));

    const selectedCard =
      visibleSelectedIds.size === 0
        ? null
        : selectors.getNarrativeViewDataForItem(
          this.props.localStore,
          this.props.viewId,
          this.state.displayData,
          visibleSelectedIds.last()
        );

    const parent = visibleSelectedIds.size === 0 ? null : selectedCard.parent;
    const narrativeIndex = visibleSelectedIds.size === 0 ? this.state.displayData.cards.size : selectedCard.index + 1;

    let extraActions = [];
    itemMapList.forEach(im => {
      extraActions.push(narrativeOperations.moveInNarrative([im.get("id")], parent, narrativeIndex));
    });

    return extraActions;
  }

  onPaste(e) {
    const {
      dispatchBatchActions,
      projectId,
      isActiveView,
      orderedNarrativeTypes,
      defaultNarrativeType,
      allTypesById,
      propertiesById,
      colorsById,
      referencesById,
      itemsById
    } = this.props;
    dispatchBatchActions(
      onPasteItems(
        e,
        projectId,
        isActiveView,
        itemMap => this.canCreateItemOfType(itemMap.get("type")),
        this.processPastingItems,
        orderedNarrativeTypes.map(t => t.id),
        defaultNarrativeType,
        typeSelectors.isNarrativeType,
        allTypesById,
        propertiesById,
        colorsById,
        referencesById,
        itemsById
      )
    );
  }

  onPasteMessageReceived(msg, clipboardData) {
    this.onPaste({ clipboardData });
  }

  onCreateItem(typeId) {
    // If this type is not allowed in narrative, then don't add it here.
    if (!this.canCreateItemOfType(typeId)) {
      return false;
    }
    // Otherwise, we can add it.
    // Do we want to add it after current selection?
    const visibleSelectedIds = this.props.selectedItemIds.filter(i => this.props.narrativeIndexById.has(i));
    if (visibleSelectedIds.size === 0) {
      this.props.onCreateItem(typeId, null, this.state.displayData.cards.size, this.props.viewId);
    } else {
      // Work out where the selection is in the tree.
      const selectedCard = selectors.getNarrativeViewDataForItem(
        this.props.localStore,
        this.props.viewId,
        this.state.displayData,
        visibleSelectedIds.last()
      );
      this.props.onCreateItem(typeId, selectedCard.parent, selectedCard.index + 1, this.props.viewId);
    }
    return true;
  }

  copyLocalStore(props) {
    const displayData = selectors.getNarrativeViewData(props.localStore, props.viewId);
    this.setState({
      displayData,
      highlightDropData: null
    });
  }

  requestViewBoundsForZoom(_zoom) {
    const size = this.state.displayData.size;
    return immutableRecords.FlatRectRecord({
      left: 0,
      width: size.width,
      right: size.width,
      top: 0,
      height: size.height,
      bottom: size.height
    });
  }

  internalDragLeave() {
    this.copyLocalStore(this.props);
  }

  internalMoveCard(itemIds, dropData, dragItem) {
    let action = dropSelectors.getActionForNarrativeOutlineDrop(
      this.props.localStore,
      itemIds,
      dropData.dropTargetId,
      dropData.dropAction
    );

    if (dragItem.sourceView === this.props.viewId) {
      action = batchUtils.batchWithAppendedActions(
        action,
        appStateOperations.userInterfaceActionPerformed(interfaceUserActions.DRAG_IN_NARRATIVE)
      );
    } else {
      action = batchUtils.batchWithAppendedActions(
        action,
        appStateOperations.userInterfaceActionPerformed(interfaceUserActions.DROP_ON_NARRATIVE)
      );
      // Since the drag was from outside, we want to trigger the filter check too
      action = batchOperations.matchFiltersAfterDropAction(action, itemIds, this.props.viewId);
    }

    if (!_.isEqual(action, this._lastAction)) {
      this._lastAction = action;
      this._lastDraggedItemIds = itemIds;
      this.setState({
        highlightDropData: dropData
      });
    }
  }

  dragPreviewDataForItemId(itemId) {
    return selectors.getNarrativeViewDataForItem(
      this.props.localStore,
      this.props.viewId,
      this.state.displayData,
      itemId
    );
  }

  requestCanDropItems(itemIds) {
    if (!itemSelectors.getItemsAreAllowedInNarrative(this.props.localStore, itemIds)) {
      return {
        canDrop: false,
        message: interfaceMessages.DRAG_DROP_NARRATIVE_ORDER_NOT_ALLOWED
      };
    }
    return { canDrop: true };
  }

  dispatchLastMoveAction() {
    if (this._lastAction) {
      this.props.onDispatchAction(this._lastAction);
      //#!if iOS !== true
      this.props.onSelectItems(this._lastDraggedItemIds);
      //#!endif

      // TO DO: Differentiate between group and reorder
      trackEvent("setItemOrderOrGroupByDragFromNarrativeView", "setItemOrderOrGroupByDrag");
    }
    this.setState({
      highlightDropData: null
    });
    this._lastAction = null;
    this._lastDraggedItemIds = null;
  }

  onItemClick(itemId, modifiers = {}, e) {
    // Note: Since the narrative cards may stacked on each other,
    // the click event may trigger by both the child and the parent.
    // So here if the click event target is from the same card,
    // do not response to the click event.
    // To avoid clicking child would select its parent.
    // See https://bitbucket.org/aeontimeline/aeon-timeline-react/pull-requests/402
    if (e.target === this._clickedCard) {
      return;
    } else {
      this._clickedCard = e.target;
    }

    const processMultiSelection = (condition = false) => {
      updateSelectedItemsUsingUpdaters(
        condition,
        itemId,
        this.props.selectedItemIds,
        this.props.onRemoveFromSelection,
        this.props.onAddToSelection,
        this.props.onSelectItems
      );
    };
    //#!if iOS === false
    if (modifiers.SHIFT && this._lastClickItemId) {
      // If this is the first shift click since some other click, record the current selection
      // as the starting point for any subsequent shift clicks
      if (!this._firstShiftClickSelection) {
        this._firstShiftClickSelection = this.props.selectedItemIds;
      }
      // Select everything from last click to current position
      const a = this.props.narrativeIndexById.get(this._lastClickItemId);
      const b = this.props.narrativeIndexById.get(itemId);
      const low = Math.min(a, b);
      const high = Math.max(a, b);
      const newIds = this.props.sortedItemIds.slice(low, high + 1).toArray();
      // Note that we can't simply add to selection, as we may want to select a lesser amount than was
      // selected with the previous shift click.
      // So instead we add this new selection to the original selection before the shift clicks began
      const allIds = this._firstShiftClickSelection.push(...newIds).toArray();
      this.props.onSelectItems(allIds);
      return;
    }

    // If we have a non-shift click, record it so we can use it as the starting point for any subsequent
    // shift clicks
    this._lastClickItemId = itemId;
    // Erase the firt shift click so we start again the next time we do a shift click
    this._firstShiftClickSelection = null;

    // And then go on and handle non-shift clicks normally!
    processMultiSelection(modifiers.CONTROL || modifiers.COMMAND);
    //#!elseif iOS === true
// processMultiSelection(this.props.screenContext.isEditMode);
    //#!endif
  }

  onSelectAvailableItem(option, parent, position) {
    this.props.onDispatchAction(narrativeOperations.moveInNarrative([option], parent, position));
    //#!if iOS != true
    this.props.onSelectItems([option]);
    //#!endif
  }

  onCreateLeafItem(parent, index, typeId) {
    if (this.props.defaultNarrativeType) {
      this.props.onCreateItem(typeId ? typeId : this.props.defaultNarrativeType, parent, index, this.props.viewId);
    }
  }

  renderSplitView = () => {
    let splitProps;
    splitProps = {
      vertical: false,
      constraints: List([
        immutableRecords.SplitConstraintsRecord({
          minSize: Dimensions.MIN_WORKVIEW_SIZE,
          canResize: false,
          preferredSize: 1000
        })
      ])
    };

    const childComponents = [
      <NarrativeCanvasView
        key="narrativeCanvasView"
        localStore={this.props.localStore}
        displayData={this.state.displayData}
        sortedItemIds={this.props.sortedItemIds}
        narrativeIndexById={this.props.narrativeIndexById}
        selectedItemIds={this.props.selectedItemIds}
        autoDeleteWhenRemoveContainer={this.props.autoDeleteWhenRemoveContainer}
        narrativeViewSettings={this.props.narrativeViewSettings}
        typesAllowedNarrativeChildren={this.props.typesAllowedNarrativeChildren}
        highlightDropData={this.state.highlightDropData}
        viewId={this.props.viewId}
        isActiveView={this.props.isActiveView}
        onInternalMoveCard={this.internalMoveCard}
        onInternalDragLeave={this.internalDragLeave}
        onDispatchLastMoveAction={this.dispatchLastMoveAction}
        onItemClick={this.onItemClick}
        onSelectItems={this.props.onSelectItems}
        onCreateItem={this.onCreateLeafItem}
        onLabelChange={this.props.onLabelChange}
        requestDragPreviewDataForItem={this.dragPreviewDataForItemId}
        requestCanDropItems={this.requestCanDropItems}
        requestViewBoundsForZoom={this.requestViewBoundsForZoom}
        fontSize={this.props.fontSize}
        availableItems={this.props.availableItems}
        orderedNarrativeTypes={this.props.orderedNarrativeTypes}
        onSelectAvailableItem={this.onSelectAvailableItem}
        labels={this.props.labels}
        autoEditBlankNodes={this.props.autoEditBlankNodes}
        onShowInspector={this.props.onShowInspector}
        typesById={this.props.allTypesById}
        isViewFilterActive={this.props.isViewFilterActive}
        isGlobalFilterActive={this.props.isGlobalFilterActive}
        onBecameActiveView={this.props.onBecameActiveView}
      />
    ];

    return (
      <SplitView key="narrativeLayoutSplitView" {...splitProps}>
        {childComponents}
      </SplitView>
    );
  };

  render() {
    const { onClick } = this.props;

    const onCanvasClick = e => {
      const clickWasLeaf =
        hasAncestorOfClass(e.target, "narrativeCard") ||
        hasAncestorOfClass(e.target, "narrativeGroupHeader") ||
        hasAncestorOfClass(e.target, "narrativeColumnHeader") ||
        hasAncestorOfClass(e.target, "icon") ||
        hasAncestorOfClass(e.target, "narrativeAddNode");
      if (!clickWasLeaf) {
        onClick();
      }
    };

    return (
      <div className="narrativeView" onClick={onCanvasClick} onPaste={this.onPaste}>
        {this.renderSplitView()}
      </div>
    );
  }
}

const mapStateToProps = (state, props) => {
  const sortedItemIds = selectors.getNarrativeViewSortedItemIds(state.present, props.viewId);
  const narrativeIndexById = listToIndexMap(sortedItemIds);
  return {
    localStore: state.present,
    // Depth first
    sortedItemIds,
    narrativeIndexById,
    selectedItemIds: uiStateSelectors.getSelectedItems(state.present),
    narrativeViewSettings: definitionSelectors.getNarrativeViewSettings(state.present),
    defaultNarrativeType: definitionSelectors.getDefaultNarrativeDocumentType(state.present),
    // eslint-disable-next-line max-len
    autoDeleteWhenRemoveContainer: narrativeSettingsSelectors.getNarrativeAutoDeleteItemWhenRemoveContainer(
      state.present
    ),
    labelCount: narrativeSettingsSelectors.getNarrativeLabelCount(state.present),
    labels: narrativeSettingsSelectors.getNarrativeLabels(state.present),
    fontSize: appStateSelectors.getFontSize(state.present),
    projectId: appSelectors.getProjectId(state.present),
    isActiveView: appStateSelectors.isViewActive(state.present, props.viewId),
    availableItems: selectors.getNarrativeViewAvailableItems(state.present),
    allTypesById: definitionSelectors.getTypesById(state.present),
    orderedNarrativeTypes: typeSelectors.getOrderedNarrativeTypes(state.present),
    typesAllowedNarrativeChildren: typeSelectors.getTypeIdsAllowedNarrativeChildren(state.present),
    propertiesById: propertySelectors.getPropertiesById(state.present),
    colorsById: colorSelectors.getColorsById(state.present),
    referencesById: referenceSelectors.getReferencesById(state.present),
    itemsById: itemSelectors.getItemsById(state.present),
    // Disable auto-edit when applying a REDO action
    autoEditBlankNodes: shouldAutoEditItems(state),

    isGlobalFilterActive: filterSelectors.isFilterWithIdActive(state.present, GLOBAL_FILTER_ID),
    isViewFilterActive: filterSelectors.isFilterWithIdActive(state.present, props.viewId)
  };
};

const readonlyActions = {
  onSelectItems: appStateOperations.selectItems,
  onAddToSelection: appStateOperations.addItemsToSelection,
  onRemoveFromSelection: appStateOperations.removeItemsFromSelection,
  onShowInspector: uiStateOperations.showInspectorModal,
  onBecameActiveView: appStateOperations.setActiveView
};

const editingActions = {
  onCreateItem: (itemType, parent, index, viewId) => {
    trackEvent("createItemFromNarrativeView", "createItem", itemType);
    return batchOperations.matchFiltersAfterCreateAction(
      batchOperations.createItemInNarrative(itemType, parent, index),
      viewId
    );
  },
  onLabelChange: itemOperations.editLabel,
  onClick: appStateOperations.selectItems,
  onDispatchAction: a => a,
  dispatchBatchActions: actions => {
    return batchOperations.reBatchActions(actions);
  },
  onShowInspector: appStateOperations.showRightPanel
};

const mapDispatchToProps = dispatch => mergeDispatchToProps(readonlyActions, editingActions, dispatch);

NarrativeView.propTypes = {
  viewId: PropTypes.string.isRequired,
  projectId: PropTypes.string.isRequired,
  isActiveView: PropTypes.bool.isRequired,
  localStore: PropTypes.object.isRequired,
  fontSize: PropTypes.number.isRequired,
  sortedItemIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  narrativeIndexById: ImmutablePropTypes.map.isRequired,
  selectedItemIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  defaultNarrativeType: PropTypes.string,
  autoDeleteWhenRemoveContainer: PropTypes.bool.isRequired,
  narrativeViewSettings: PropTypeShapes.narrativeViewSettings.isRequired,

  typesAllowedNarrativeChildren: ImmutablePropTypes.listOf(PropTypes.string).isRequired,

  onSelectItems: PropTypes.func.isRequired,
  onAddToSelection: PropTypes.func.isRequired,
  onRemoveFromSelection: PropTypes.func.isRequired,
  onCreateItem: PropTypes.func.isRequired,
  onLabelChange: PropTypes.func,
  onDispatchAction: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
  labelCount: PropTypes.number.isRequired,
  labels: ImmutablePropTypes.list.isRequired,
  availableItems: ImmutablePropTypes.list,
  allTypesById: ImmutablePropTypes.map.isRequired,
  orderedNarrativeTypes: ImmutablePropTypes.list.isRequired,
  autoEditBlankNodes: PropTypes.bool.isRequired,
  isViewFilterActive: PropTypes.bool,
  isGlobalFilterActive: PropTypes.bool,
  onBecameActiveView: PropTypes.func.isRequired,

  // copy paste
  dispatchBatchActions: PropTypes.func,
  propertiesById: ImmutablePropTypes.map,
  colorsById: ImmutablePropTypes.map,
  referencesById: ImmutablePropTypes.map,
  itemsById: ImmutablePropTypes.map,

  onShowInspector: PropTypes.func,

  // Workspace context
  workViewRegistration: PropTypes.object.isRequired,

  //#!if iOS === true
  // From withScreenContext
// screenContext: PropTypes.object
  //#!endif
};

const normalView = withWorkspaceContext(connect(mapStateToProps, mapDispatchToProps)(NarrativeView));

let exportedView = normalView;
//#!if iOS === true
// exportedView = withScreenContext(normalView);
//#!endif

export default exportedView;
